<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import { Loader2, ShieldCheck, CreditCard } from 'lucide-vue-next'
import { loadStripe } from '@stripe/stripe-js'
import type { Stripe, StripeElements, StripePaymentElement } from '@stripe/stripe-js'
import axios from 'axios'

const props = defineProps<{
  previewPath: string
  paymentPath: string
  confirmPath: string
  stripePublishableKey: string
  // リダイレクト型決済（コンビニ/銀行振込等）でStripeから戻ってくる先。
  // GET可能な予約詳細URL（change_redirect_status=succeeded付き）を渡す。
  // 未指定時は従来どおりconfirmPathを使う。
  returnUrl?: string
}>()

// 状態: idle(初期) / paying(差額決済の入力待ち)
const phase = ref<'idle' | 'paying'>('idle')
const loading = ref(false)
const error = ref('')
const diffAmount = ref(0)

let stripe: Stripe | null = null
let elements: StripeElements | null = null
let paymentElement: StripePaymentElement | null = null
let clientSecret = ''

const csrfToken = computed(() => {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') || ''
})

function headers() {
  return { headers: { 'X-CSRF-Token': csrfToken.value } }
}

function formatPrice(cents: number): string {
  return cents.toLocaleString('ja-JP')
}

// reservation-form の入力値から送信ペイロードを構築する
function payload() {
  const startField = document.querySelector<HTMLSelectElement>('[name="reservation[start_time]"]')
  const endField = document.querySelector<HTMLSelectElement | HTMLInputElement>('[name="reservation[end_time]"]')
  const guestsField = document.querySelector<HTMLInputElement>('[name="reservation[number_of_guests]"]')

  return {
    start_time: startField?.value || '',
    end_time: endField?.value || '',
    number_of_guests: parseInt(guestsField?.value || '1', 10) || 1,
  }
}

function redirectTo(url: string) {
  window.location.href = url
}

// 1. プレビューで kind を判定し、増額なら差額決済へ、それ以外は即時確定。
async function startChange() {
  if (loading.value) return
  loading.value = true
  error.value = ''

  try {
    const reservation = payload()
    const preview = await axios.post(props.previewPath, { reservation }, headers())
    diffAmount.value = Math.abs(preview.data.new_total_cents - preview.data.old_total_cents)

    if (preview.data.kind === 'charge') {
      await initiateCharge(reservation)
    } else {
      await confirmWithoutCharge(reservation)
    }
  } catch (e: unknown) {
    error.value = extractError(e, '変更の確認に失敗しました')
    loading.value = false
  }
}

// 減額/同額: payment_intent_id 無しで即時確定。
async function confirmWithoutCharge(reservation: ReturnType<typeof payload>) {
  const done = await axios.post(props.confirmPath, { reservation }, headers())
  redirectTo(done.data.redirect_url)
}

// 増額: 差額 PaymentIntent を作成し Payment Element をマウントする。
async function initiateCharge(reservation: ReturnType<typeof payload>) {
  const pay = await axios.post(props.paymentPath, { reservation }, headers())
  clientSecret = pay.data.client_secret

  stripe = await loadStripe(props.stripePublishableKey)
  if (!stripe || !clientSecret) {
    error.value = '決済システムの初期化に失敗しました'
    loading.value = false
    return
  }

  phase.value = 'paying'
  loading.value = false
  await nextTick()
  mountPaymentElement()
}

function mountPaymentElement() {
  if (!stripe || !clientSecret) return
  if (paymentElement) paymentElement.destroy()

  elements = stripe.elements({
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#5a3a26',
        borderRadius: '0px',
        fontFamily: 'inherit',
      },
    },
    locale: 'ja',
  })

  paymentElement = elements.create('payment')
  paymentElement.mount('#change-payment-element')
}

// 2. 差額決済を確定 → 成功後 payment_intent_id で変更を確定。
async function payAndConfirm() {
  if (!stripe || !elements || loading.value) return
  loading.value = true
  error.value = ''

  // リダイレクト型決済はGET可能な予約詳細へ戻す（POST専用のconfirmPathは302で開けない）。
  // 通常のカード決済は redirect: 'if_required' によりreturn_urlへ遷移しないため、
  // returnUrl未指定時のフォールバックとしてconfirmPathを使う。
  const returnUrl = props.returnUrl || props.confirmPath
  const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
    elements,
    confirmParams: {
      return_url: `${window.location.origin}${returnUrl}`,
    },
    redirect: 'if_required',
  })

  if (stripeError) {
    error.value = stripeError.message || '決済に失敗しました'
    loading.value = false
    return
  }

  if (paymentIntent && paymentIntent.status === 'succeeded') {
    try {
      // client_secret から payment_intent_id を切り出す（"pi_xxx_secret_yyy"）
      const piId = paymentIntent.id || clientSecret.split('_secret')[0]
      const done = await axios.post(props.confirmPath, { payment_intent_id: piId }, headers())
      redirectTo(done.data.redirect_url)
    } catch (e: unknown) {
      // confirm が失敗しても Webhook で確定されるため予約詳細へ誘導する
      error.value = extractError(e, '変更の確定に失敗しました。予約詳細をご確認ください。')
      loading.value = false
    }
  } else {
    loading.value = false
    error.value = '決済処理中です。しばらくお待ちください。'
  }
}

function extractError(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e) && e.response?.data) {
    return e.response.data.error || e.response.data.message || fallback
  }
  return fallback
}
</script>

<template>
  <div>
    <!-- エラー表示 -->
    <div
      v-if="error"
      class="mb-4 p-3 bg-red-50 border-2 border-red-300 text-red-700 text-sm font-bold"
    >
      {{ error }}
    </div>

    <!-- 初期: 変更を確定ボタン -->
    <template v-if="phase === 'idle'">
      <button
        type="button"
        class="w-full retro-btn retro-btn-primary text-sm cursor-pointer"
        :disabled="loading"
        @click="startChange"
      >
        <span
          v-if="loading"
          class="inline-flex items-center"
        >
          <Loader2 class="w-4 h-4 animate-spin mr-2" />
          確認中...
        </span>
        <span v-else>変更を確定</span>
      </button>
    </template>

    <!-- 増額: 差額決済フォーム -->
    <template v-else>
      <div class="retro-card p-6">
        <div class="flex items-center gap-2 mb-4">
          <CreditCard
            class="w-5 h-5 text-[#5a3a26]"
            :stroke-width="2.5"
          />
          <h2 class="text-lg font-bold text-[#5a3a26]">
            差額のお支払い
          </h2>
        </div>

        <div class="mb-4 flex justify-between items-center">
          <span class="font-bold text-[#5a3a26]">追加料金</span>
          <span class="text-lg font-black text-red-600">+¥{{ formatPrice(diffAmount) }}</span>
        </div>

        <!-- Stripe Payment Element -->
        <div
          id="change-payment-element"
          class="mb-6"
        />

        <div class="flex items-center gap-2 mb-4 text-xs text-[#8b5a3b]">
          <ShieldCheck
            class="w-4 h-4"
            :stroke-width="2"
          />
          <span>お支払い情報はStripeで安全に処理されます</span>
        </div>

        <button
          type="button"
          class="w-full retro-btn retro-btn-primary text-sm cursor-pointer"
          :disabled="loading"
          @click="payAndConfirm"
        >
          <span
            v-if="loading"
            class="inline-flex items-center"
          >
            <Loader2 class="w-4 h-4 animate-spin mr-2" />
            処理中...
          </span>
          <span v-else>¥{{ formatPrice(diffAmount) }} を支払って変更を確定</span>
        </button>
      </div>
    </template>
  </div>
</template>
